
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Navigation, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PropertyLocationMapProps {
  latitude?: number;
  longitude?: number;
  address: string;
  title: string;
}

const PropertyLocationMap = ({ latitude, longitude, address, title }: PropertyLocationMapProps) => {
  const handleOpenInMaps = () => {
    if (latitude && longitude) {
      // فتح الموقع في خرائط جوجل
      const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(mapsUrl, '_blank');
    } else if (address) {
      // البحث بالعنوان في خرائط جوجل
      const mapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(address)}`;
      window.open(mapsUrl, '_blank');
    }
  };

  const handleOpenInAppleMaps = () => {
    if (latitude && longitude) {
      const appleMapsUrl = `http://maps.apple.com/?q=${latitude},${longitude}`;
      window.open(appleMapsUrl, '_blank');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-right flex items-center gap-2">
          <MapPin className="h-5 w-5 text-green-600" />
          موقع العقار
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* منطقة الخريطة التفاعلية */}
          <div className="relative h-64 bg-gradient-to-br from-blue-50 to-green-50 rounded-lg overflow-hidden border-2 border-dashed border-gray-200">
            {latitude && longitude ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  {/* رمز موقع كبير */}
                  <div className="relative mb-4">
                    <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto shadow-lg animate-pulse">
                      <MapPin className="h-8 w-8 text-white" />
                    </div>
                    {/* دوائر الموجات */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-16 border-4 border-red-300 rounded-full animate-ping"></div>
                    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-12 h-12 border-2 border-red-200 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
                  </div>
                  
                  <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 mb-3">{address}</p>
                  
                  {/* عرض الإحداثيات */}
                  <div className="bg-white/80 backdrop-blur rounded-lg p-3 text-xs text-gray-700">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="font-semibold">خط العرض:</span>
                        <br />
                        <span className="font-mono">{latitude.toFixed(6)}</span>
                      </div>
                      <div>
                        <span className="font-semibold">خط الطول:</span>
                        <br />
                        <span className="font-mono">{longitude.toFixed(6)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="font-medium">موقع العقار</p>
                  <p className="text-sm mt-1">{address}</p>
                  <p className="text-xs mt-2 text-gray-400">الإحداثيات غير متوفرة</p>
                </div>
              </div>
            )}
            
            {/* تأثير الخريطة */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-green-500/5 pointer-events-none" />
            
            {/* شبكة خفيفة لمحاكاة الخريطة */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="grid grid-cols-8 grid-rows-6 h-full">
                {Array.from({ length: 48 }).map((_, i) => (
                  <div key={i} className="border border-gray-400"></div>
                ))}
              </div>
            </div>
          </div>

          {/* معلومات الموقع */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2 text-right flex items-center gap-2">
              <MapPin className="h-4 w-4 text-green-600" />
              تفاصيل الموقع
            </h4>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex justify-between">
                <span className="font-medium">العنوان:</span>
                <span className="text-right flex-1 mr-2">{address}</span>
              </div>
              
              {latitude && longitude && (
                <>
                  <div className="flex justify-between">
                    <span className="font-medium">خط العرض:</span>
                    <span className="font-mono">{latitude.toFixed(6)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">خط الطول:</span>
                    <span className="font-mono">{longitude.toFixed(6)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* أزرار فتح الخرائط */}
          <div className="space-y-2">
            <Button 
              onClick={handleOpenInMaps}
              className="w-full flex items-center gap-2"
              variant="outline"
            >
              <ExternalLink className="h-4 w-4" />
              فتح في خرائط جوجل
            </Button>
            
            {latitude && longitude && (
              <Button 
                onClick={handleOpenInAppleMaps}
                className="w-full flex items-center gap-2"
                variant="outline"
              >
                <Navigation className="h-4 w-4" />
                فتح في خرائط آبل
              </Button>
            )}
          </div>

          {/* ملاحظة */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm text-blue-800 text-right">
              <strong>ملاحظة:</strong> انقر على الأزرار أعلاه لفتح الموقع في تطبيقات الخرائط المختلفة للحصول على اتجاهات دقيقة.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyLocationMap;
